import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { 
  Check, 
  ArrowRight, 
  Sparkles,
  HelpCircle
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const plans = [
  {
    name: 'Monthly',
    description: 'Flexible monthly subscription',
    price: '£59.99',
    period: '/month',
    monthly: null,
    highlight: false,
    badge: 'Popular',
    badgeColor: 'bg-primary text-white',
    budgetType: 'monthly',
    features: [
      'Custom designed website',
      'Mobile responsive design',
      'SEO optimization included',
      'SSL certificate',
      'Hosting included',
      '24/7 support',
      'Monthly maintenance',
      'Content updates (2/month)',
      'Free domain worth up to £20',
      'Minimum 12 month term'
    ],
    note: 'Minimum 12 month term. Website remains active while subscription is active.',
    cta: 'Get Started'
  },
  {
    name: 'Setup + Hosting',
    description: 'Lower monthly cost',
    price: '£500',
    period: 'one-time',
    monthly: '£24.99/month',
    highlight: false,
    badge: null,
    budgetType: 'monthly',
    features: [
      'Custom designed website',
      'Mobile responsive design',
      'SEO optimization included',
      'SSL certificate',
      'Hosting included',
      '24/7 support',
      'Monthly maintenance',
      'Content updates (2/month)',
      'Free domain worth up to £20',
      'Priority setup'
    ],
    note: 'Pay once for setup, then just £24.99/month for hosting and support.',
    cta: 'Get Started'
  },
  {
    name: 'Full Purchase',
    description: 'Own your website forever',
    price: '£1,399.99',
    period: 'one-time',
    monthly: null,
    highlight: true,
    badge: 'Best Value',
    badgeColor: 'bg-green-500 text-white',
    budgetType: 'full',
    features: [
      'Everything included, plus:',
      'Full website ownership',
      'Source code access',
      'Priority support during build',
      'Custom domain setup assistance',
      'Advanced SEO package',
      'Social media integration',
      'No monthly fees ever',
      'Host anywhere you choose',
      'Full code handover'
    ],
    note: 'You own the website and source code forever. You provide your own hosting - we\'ll help you set it up.',
    cta: 'Get Started'
  }
];

const faqs = [
  {
    question: 'What happens if I cancel my monthly subscription?',
    answer: 'If you cancel your monthly subscription (£59.99/month), your website will become inactive and visitors won\'t be able to access it. You can reactivate at any time by resuming your subscription. If you\'ve paid for the Full Purchase, you own the website and it will remain active forever.'
  },
  {
    question: 'What\'s the difference between Monthly and Setup + Hosting?',
    answer: 'The Monthly plan (£59.99/month) has no upfront cost - just pay monthly. The Setup + Hosting plan requires £500 upfront but then only £24.99/month ongoing. If you plan to keep your website for more than 15 months, Setup + Hosting works out cheaper in the long run.'
  },
  {
    question: 'Can I upgrade from subscription to full purchase?',
    answer: 'This is definitely possible! Please reach out to us over email and we can help sort this for you at a reduced cost.'
  },
  {
    question: 'What\'s included in the monthly maintenance?',
    answer: 'Monthly maintenance includes security updates, performance optimization, SSL certificate renewal, hosting, and up to 2 content updates per month.'
  },
  {
    question: 'Do I need to provide content for my website?',
    answer: 'Our AI can generate professional content based on your business information and reviews. However, you can provide your own content if you prefer. We\'ll work with whatever you\'re comfortable with.'
  },
  {
    question: 'How long does it take to build a website?',
    answer: 'Most websites are ready within 48-72 hours. More complex projects may take up to a week. We\'ll give you an accurate timeline after understanding your requirements.'
  },
  {
    question: 'What happens with hosting for the Full Purchase?',
    answer: 'With the Full Purchase, you receive the complete source code and own the website outright. You\'ll need to arrange your own hosting - we recommend providers like Vercel, Netlify, or any web host of your choice. We\'ll help you with the initial setup and deployment.'
  }
];

const PricingPage = () => {
  useEffect(() => {
    document.title = 'Pricing | TryWeb - Affordable Website Design from £59.99/month';
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative py-24 hero-glow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Simple, Transparent Pricing</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            <span className="text-gradient">Choose Your Plan</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Affordable pricing options designed to fit your business needs. 
            No hidden fees, no surprises.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 pb-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`relative rounded-2xl p-6 ${
                  plan.highlight 
                    ? 'bg-card/80 border-2 border-primary/50 shadow-[0_0_30px_rgba(99,102,241,0.15)]' 
                    : 'bg-card/50 border border-white/5'
                }`}
              >
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium ${plan.badgeColor || 'bg-primary text-white'}`}>
                    {plan.badge}
                  </div>
                )}
                
                <div className="mb-5">
                  <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                  <p className="text-muted-foreground text-sm">{plan.description}</p>
                </div>

                <div className="mb-5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground text-sm">{plan.period}</span>
                  </div>
                  {plan.monthly && (
                    <p className="text-sm text-primary mt-1">+ {plan.monthly}</p>
                  )}
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlight ? 'text-primary' : 'text-secondary'}`} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-xs text-muted-foreground mb-5 p-2.5 bg-white/5 rounded-lg">
                  {plan.note}
                </p>

                <Link to={`/get-started?budget=${plan.budgetType}`}>
                  <Button 
                    className="w-full glow-primary"
                    variant="default"
                    size="lg"
                    data-testid={`pricing-${plan.name.toLowerCase().replace(' ', '-')}-cta`}
                  >
                    {plan.cta} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extra Additions */}
      <section className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold mb-4 text-primary">Need Extra Features?</h3>
            <p className="text-muted-foreground mb-6">
              All plans can be enhanced with additional functionality to suit your business needs.<br />
              Popular add-ons include <strong className="text-foreground">custom email addresses</strong>, <strong className="text-foreground">e-commerce &amp; online payments</strong>, <strong className="text-foreground">booking systems</strong>, <strong className="text-foreground">live chat</strong>, and more.
            </p>
            <Link to="/contact">
              <Button variant="default" className="glow-primary" data-testid="pricing-help-cta">
                Get in Touch to Discuss Your Requirements
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-card/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
              <HelpCircle className="w-4 h-4 text-secondary" />
              <span className="text-sm font-medium text-secondary">FAQs</span>
            </div>
            <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-muted-foreground">
              Got questions? We've got answers.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-card/50 border border-white/5 rounded-xl px-6"
              >
                <AccordionTrigger className="text-left hover:no-underline py-4" data-testid={`faq-${index}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-8">
            Join hundreds of businesses that have transformed their online presence with TryWeb.
          </p>
          <Link to="/contact">
            <Button size="lg" className="glow-primary gap-2" data-testid="pricing-final-cta">
              Contact Us Today <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </PageLayout>
  );
};

export default PricingPage;
